// Page Me — page text: graphemes, emoji, validation. Pure.
//
// A page is 160 GRAPHEMES, not 160 UTF-16 units: "👨‍👩‍👧" is one character to
// the person typing it, and the server counts the same way.

export const MAX_GRAPHEMES = 160;

const segmenter = typeof Intl !== 'undefined' && Intl.Segmenter
  ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
  : null;

/** "07734 👋" → ['0','7','7','3','4',' ','👋'] */
export function graphemes(str) {
  const s = String(str ?? '');
  if (!s) return [];
  if (segmenter) return Array.from(segmenter.segment(s), (x) => x.segment);
  return Array.from(s);
}

export function countGraphemes(str) {
  return graphemes(str).length;
}

const PICTO = /\p{Extended_Pictographic}|\p{Regional_Indicator}/u;
const KEYCAP = new RegExp('\\u20E3', 'u');
const EMOJI_PRESENTATION = new RegExp('\\uFE0F', 'u');

/** Is this grapheme drawn as an emoji (→ <PixelEmoji>)? Plain digits and "#" are not. */
export function isEmoji(g) {
  const s = String(g ?? '');
  if (!s) return false;
  if (KEYCAP.test(s)) return true;
  if (PICTO.test(s)) {
    // ©, ®, ™, ‼ etc. are Extended_Pictographic but render as text unless
    // asked for emoji presentation; one code point and no FE0F → text.
    const cps = Array.from(s);
    if (cps.length === 1 && s.codePointAt(0) < 0x2000 && !EMOJI_PRESENTATION.test(s)) return false;
    return true;
  }
  return false;
}

// C0/C1 controls, line/paragraph separators, bidi overrides and isolates, BOM —
// the same set the server rejects. ZWJ and variation selectors stay.
const CONTROL = new RegExp('[\\p{Cc}\\u2028\\u2029\\u202A-\\u202E\\u2066-\\u2069\\uFEFF]', 'u');

/** The body as it will be sent: NFC, newlines/tabs folded to spaces, trimmed. */
export function normalizeBody(str) {
  return String(str ?? '').normalize('NFC').replace(/[\r\n\t]+/g, ' ').trim();
}

/** An error sentence (app voice), or null when the body can be sent. */
export function validateBody(str) {
  const s = normalizeBody(str);
  if (!s) return 'Write a page first.';
  if (CONTROL.test(s)) return "Pages can't contain control characters.";
  if (countGraphemes(s) > MAX_GRAPHEMES) return `Pages are ${MAX_GRAPHEMES} characters at most.`;
  return null;
}

/** Cut to at most `max` graphemes (for inputs that must never exceed the cap). */
export function clipGraphemes(str, max = MAX_GRAPHEMES) {
  const g = graphemes(str);
  return g.length <= max ? String(str ?? '') : g.slice(0, max).join('');
}
